// @ts-nocheck
/**
 * 鍵バリデーションモジュール
 * 鍵として選択可能かを検証
 * @module twinKey/core/keySystem/keyValidator
 */

// ============================================================
// メイン関数
// ============================================================

/**
 * 鍵として選択可能かを検証
 * @param {Object} card - カード情報
 * @param {Object} playerData - プレイヤーデータ
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateKeyCard(card, playerData) {
  if (!card) {
    return { valid: false, reason: 'カードが指定されていません' };
  }

  if (!playerData) {
    return { valid: false, reason: 'プレイヤーデータがありません' };
  }

  // 所持チェック
  // コレクションは配列形式: [{ cardId, rarity, quantity }, ...]
  const collection = playerData.collection || [];
  const owned = collection.find(c => c.cardId === card.id && c.rarity === card.rarity);
  if (!owned) {
    return { valid: false, reason: 'カードを所持していません' };
  }

  // 枚数チェック（最低1枚必要）
  if (owned.quantity < 1) {
    return { valid: false, reason: 'カードの所持数が不足しています' };
  }

  // 全カードタイプ使用可能（monster/magic/field/phasecard）
  return { valid: true };
}

/**
 * 鍵ペアの検証
 * @param {Object} key1 - 鍵1
 * @param {Object} key2 - 鍵2
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateKeyPair(key1, key2) {
  if (!key1 || !key2) {
    return { valid: false, reason: '鍵を2枚選択してください' };
  }

  // 同じカードインスタンスは不可（uniqueKey または uniqueId で比較）
  const key1Id = key1.uniqueKey || key1.uniqueId;
  const key2Id = key2.uniqueKey || key2.uniqueId;
  if (key1Id && key2Id && key1Id === key2Id) {
    return { valid: false, reason: '同じカードは選択できません' };
  }

  // 同じカードID（異なるレアリティ）は許可される
  // これにより「同名異レア」の組み合わせが可能

  return { valid: true };
}

/**
 * 鍵カードが使用済みかチェック
 * （将来の機能: クールダウンシステム）
 * @param {Object} card - カード情報
 * @param {Object} playerData - プレイヤーデータ
 * @returns {{ available: boolean, cooldownRemaining?: number }}
 */
export function checkKeyCooldown(card, playerData) {
  // Phase 1ではクールダウンなし
  // 将来的には使用後にクールダウンを設定可能
  const cooldowns = playerData.twinKey?.keyCooldowns || {};
  const cooldown = cooldowns[card.uniqueId];

  if (cooldown && cooldown > Date.now()) {
    const remaining = Math.ceil((cooldown - Date.now()) / (1000 * 60 * 60)); // 時間単位
    return { available: false, cooldownRemaining: remaining };
  }

  return { available: true };
}

/**
 * 探索開始の検証（鍵ペア + パーティ）
 * @param {Object} key1 - 鍵1
 * @param {Object} key2 - 鍵2
 * @param {Object} party - パーティ情報
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateExpedition(key1, key2, party) {
  // 鍵ペアの検証
  const keyValidation = validateKeyPair(key1, key2);
  if (!keyValidation.valid) {
    return keyValidation;
  }

  // パーティの検証
  if (!party) {
    return { valid: false, reason: 'パーティが選択されていません' };
  }

  if (!party.members || party.members.length === 0) {
    return { valid: false, reason: 'パーティにメンバーがいません' };
  }

  // 最低1体、最大5体
  const memberCount = party.members.filter(m => m !== null).length;
  if (memberCount < 1) {
    return { valid: false, reason: 'パーティに最低1体のモンスターが必要です' };
  }

  if (memberCount > 5) {
    return { valid: false, reason: 'パーティは5体までです' };
  }

  return { valid: true };
}

// ============================================================
// 鍵の特性チェック
// ============================================================

/**
 * 鍵カードが禁忌カードかチェック
 * @param {Object} card - カード情報
 * @returns {boolean}
 */
export function isForbiddenKey(card) {
  return card.forbidden === true || card.rarity === 'GR';
}

/**
 * 鍵ペアが特別な組み合わせかチェック
 * （Phase 3で実装: ストーリーダンジョン解禁など）
 * @param {Object} key1 - 鍵1
 * @param {Object} key2 - 鍵2
 * @returns {{ isSpecial: boolean, specialType?: string, description?: string }}
 */
export function checkSpecialCombination(key1, key2) {
  // Phase 1では特別組み合わせ未実装
  // Phase 3でストーリーダンジョン連動として実装予定

  // 同名異レアのチェック（将来用）
  if (key1.cardId === key2.cardId && key1.rarity !== key2.rarity) {
    return {
      isSpecial: true,
      specialType: 'same_card_different_rarity',
      description: '同名異レア：記憶の重なり',
    };
  }

  // 禁忌ペアのチェック
  if (isForbiddenKey(key1) && isForbiddenKey(key2)) {
    return {
      isSpecial: true,
      specialType: 'double_forbidden',
      description: '禁忌の共鳴：最深部への扉',
    };
  }

  return { isSpecial: false };
}

/**
 * 鍵カードの品質スコアを計算
 * （ダンジョン報酬に影響）
 * @param {Object} key1 - 鍵1
 * @param {Object} key2 - 鍵2
 * @returns {number} 品質スコア（0-100）
 */
export function calculateKeyQuality(key1, key2) {
  // レアリティベースの品質
  const rarityScores = {
    'C': 5, 'UC': 10, 'R': 20, 'SR': 35, 'UR': 50,
    'HR': 65, 'SEC': 75, 'ALT': 75, 'SP': 85, 'GR': 100,
  };

  const score1 = rarityScores[key1.rarity] || 5;
  const score2 = rarityScores[key2.rarity] || 5;

  // 平均 + シナジーボーナス
  let quality = (score1 + score2) / 2;

  // 同属性ボーナス
  if (key1.attribute === key2.attribute) {
    quality += 5;
  }

  return Math.min(100, Math.round(quality));
}
